/**
 * Public IdP-resolve API client (TQ #6893). Unauthenticated — used before the
 * user has a Keycloak session. Given an email address, the backend returns the
 * matching Keycloak IdP broker alias so the email-first login can jump straight
 * to the correct broker (kc_idp_hint) and skip the broker-selection page.
 *
 * Same convention as services/registrationApi.ts (public, no Bearer/JWT).
 */
import type { AxiosError } from 'axios'
import { createApiClient } from '../lib/apiClient'

// Same convention as services/api.ts: relative /api in prod (nginx proxy),



const client = createApiClient({
  timeout: 15000,
  headers: { 'Content-Type': 'application/json' },
})

export interface IdpResolveResponse {
  alias: string
}

// Fallback broker for unknown domains (scemail.de + catch-all per backend
// contract). Used when idp-resolve fails so the flow never hangs.
export const FALLBACK_IDP_ALIAS = 'personal-users'

export const IdpResolveAPI = {
  /**
   * Resolve the Keycloak IdP broker alias for an email address.
   * GET /api/public/idp-resolve?email=<email> -> { "alias": "<idp-alias>" }
   * Verified aliases: it-sce, personal-users, eza, bum, bib, glasbrenner.
   */
  async resolve(email: string): Promise<string> {
    try {
      const res = await client.get<IdpResolveResponse>(
        '/public/idp-resolve',
        { params: { email } }
      )
      const alias = res.data?.alias?.trim()
      if (!alias) {
        throw new Error('Leere IdP-Antwort')
      }
      return alias
    } catch (error) {
      const axiosError = error as AxiosError
      // Surface a clean error; callers decide on the fallback broker.
      throw new Error(
        axiosError.message || 'IdP-Auflösung fehlgeschlagen'
      )
    }
  },
}
